// src/pages/templates/TemplateHubPage.tsx
// Template Hub — browse, search, and manage templates.

import React, { useState, useMemo, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import {
  Card,
  CardContent,
  ErrorAlert,
  Input,
} from '@react/components';

import type {
  TemplateDefinition,
  TemplateCategory,
  TemplateScope,
  TemplateSortField,
} from '@/features/templates/types';

import {
  TEMPLATE_CATEGORIES,
  TEMPLATE_SCOPES,
  filterTemplates,
  sortTemplates,
} from '@/features/templates/logic';

import { TemplateCard } from '@/components/TemplateCard';

// ---------------------------------------------------------------------------
// SVG icon helpers
// ---------------------------------------------------------------------------

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props — in a real app, templates come from a store / API
// ---------------------------------------------------------------------------

interface TemplateHubPageProps {
  templates?: TemplateDefinition[];
  isLoading?: boolean;
  error?: string | null;
  userId?: string;
  userRole?: string;
  onCreateTemplate?: () => void;
  onUseTemplate?: (template: TemplateDefinition) => void;
  onEditTemplate?: (template: TemplateDefinition) => void;
  onDeleteTemplate?: (template: TemplateDefinition) => void;
  onDuplicateTemplate?: (template: TemplateDefinition) => void;
  onDismissError?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TemplateHubPage: React.FC<TemplateHubPageProps> = ({
  templates = [],
  isLoading = false,
  error = null,
  userId = '',
  userRole = 'member',
  onCreateTemplate,
  onUseTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
  onDismissError,
}) => {
  const { t } = useI18n();

  // -- State ----------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | undefined>(undefined);
  const [activeScope, setActiveScope] = useState<TemplateScope | undefined>(undefined);
  const [sortBy, setSortBy] = useState<TemplateSortField>('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // -- Computed values ------------------------------------------------------
  const filtered = useMemo(
    () =>
      sortTemplates(
        filterTemplates(templates, {
          category: activeCategory,
          scope: activeScope,
          search,
        }),
        sortBy,
      ),
    [templates, activeCategory, activeScope, search, sortBy],
  );

  // -- Handlers -------------------------------------------------------------
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleCategoryClick = useCallback((cat: TemplateCategory | undefined) => {
    setActiveCategory(prev => (prev === cat ? undefined : cat));
  }, []);

  const handleScopeClick = useCallback((scope: TemplateScope | undefined) => {
    setActiveScope(prev => (prev === scope ? undefined : scope));
  }, []);

  const canEdit = useCallback(
    (tpl: TemplateDefinition) => {
      if (userRole === 'admin' || userRole === 'owner') return true;
      if (tpl.scope === 'system') return false;
      return tpl.created_by === userId;
    },
    [userId, userRole],
  );

  const canDelete = useCallback(
    (tpl: TemplateDefinition) => {
      if (userRole === 'admin' || userRole === 'owner') return true;
      if (tpl.scope === 'system' || tpl.scope === 'organization') return false;
      return tpl.created_by === userId;
    },
    [userId, userRole],
  );

  // -- Sort options ---------------------------------------------------------
  const SORT_OPTIONS: { id: TemplateSortField; labelKey: string }[] = [
    { id: 'name', labelKey: 'templates.sort.name' },
    { id: 'usage_count', labelKey: 'templates.sort.usageCount' },
    { id: 'created_at', labelKey: 'templates.sort.createdAt' },
    { id: 'updated_at', labelKey: 'templates.sort.updatedAt' },
  ];

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('templates.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('templates.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder={t('templates.searchPlaceholder')}
              className="pl-9 w-64"
            />
          </div>

          {/* Sort dropdown (native) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSortDropdownOpen(prev => !prev)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('templates.sortBy')}
              <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {sortDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setSortDropdownOpen(false)}
                />
                <div className="absolute right-0 mt-1 z-20 w-44 rounded-md border border-border bg-popover shadow-md">
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setSortBy(opt.id);
                        setSortDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-muted ${
                        sortBy === opt.id ? 'text-primary font-medium' : 'text-foreground'
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Create button */}
          {onCreateTemplate && (
            <button
              type="button"
              onClick={onCreateTemplate}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <PlusIcon className="h-4 w-4" />
              {t('templates.create')}
            </button>
          )}
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="border-b border-border" role="tablist" aria-label={t('templates.categories')}>
        <div className="flex gap-0 -mb-px overflow-x-auto">
          <button
            type="button"
            role="tab"
            aria-selected={activeCategory === undefined}
            onClick={() => handleCategoryClick(undefined)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeCategory === undefined
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {t('templates.filter.all')}
          </button>
          {TEMPLATE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Scope filter pills */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleScopeClick(undefined)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            activeScope === undefined
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
          }`}
        >
          {t('templates.filter.allScopes')}
        </button>
        {TEMPLATE_SCOPES.map(scope => (
          <button
            key={scope.id}
            type="button"
            onClick={() => handleScopeClick(scope.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              activeScope === scope.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            {t(scope.labelKey)}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <ErrorAlert error={error} dismissible onDismiss={onDismissError} />
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                    <div className="h-3 w-48 bg-muted rounded animate-pulse" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="h-5 w-16 bg-muted rounded-full animate-pulse" />
                  <div className="h-5 w-20 bg-muted rounded-full animate-pulse" />
                </div>
                <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filtered.length === 0 && !error && (
        <Card>
          <CardContent className="py-16 text-center">
            <TemplateIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium text-foreground">
              {t('templates.empty.title')}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {search || activeCategory || activeScope
                ? t('templates.empty.filtered')
                : t('templates.empty.description')}
            </p>
            {onCreateTemplate && !search && !activeCategory && !activeScope && (
              <button
                type="button"
                onClick={onCreateTemplate}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <PlusIcon className="h-4 w-4" />
                {t('templates.createFirst')}
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Template cards grid */}
      {!isLoading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(tpl => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              onUse={onUseTemplate ? () => onUseTemplate(tpl) : undefined}
              onEdit={canEdit(tpl) && onEditTemplate ? () => onEditTemplate(tpl) : undefined}
              onDelete={canDelete(tpl) && onDeleteTemplate ? () => onDeleteTemplate(tpl) : undefined}
              onDuplicate={onDuplicateTemplate ? () => onDuplicateTemplate(tpl) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { TemplateHubPage };
export default TemplateHubPage;
